import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, moneyAmount } from '../../stores/coreSession';
import { drawTradeDialog, deltaLabel } from './trade_dialog';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';
import { trackEvent } from '../../utils/analytics';

const FALLBACK_ASSETS = [
  { id: 'weed', symbol: 'WEED', name: 'WEED', sub: 'LEGACY COMMODITY' },
  { id: 'quaaludes', symbol: 'LUDES', name: 'LUDES', sub: 'LEGACY COMMODITY' },
  { id: 'speed', symbol: 'SPEED', name: 'SPEED', sub: 'LEGACY COMMODITY' },
  { id: 'cocaine', symbol: 'COCAINE', name: 'COCAINE', sub: 'LEGACY COMMODITY' },
];

const LOW_CASH_THRESHOLD = 20;
const MAX_QTY = 20;

const state = {
  assets: FALLBACK_ASSETS,
  selected: 0,
  status: 'NOT SYNCED',
  loading: false,
  loadedOnce: false,
  tradeOpen: false,
  side: 'BUY',
  qty: 1,
  quote: null,
  quoteError: null,
  receipt: null,
  error: null,
};

let loadPromise = null;
let quoteSeq = 0;

if (typeof window !== 'undefined') {
  window.addEventListener('dcw:escape', (event) => {
    if (state.tradeOpen) {
      state.tradeOpen = false;
      event.preventDefault();
    }
  });
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function money(value) {
  if (value?.amount == null) return '--';
  return `$${value.amount}`;
}

function shortName(value) {
  return String(value || '--').replace(/\s*\(.*\)$/, '').toUpperCase();
}

function assetFromContent(asset) {
  return {
    id: asset.assetId,
    symbol: String(asset.assetId || '').toUpperCase(),
    name: shortName(asset.streetName || asset.name || asset.assetId),
    sub: asset.market === 'public' ? 'PUBLIC MARKET' : String(asset.market || '').toUpperCase(),
  };
}

function owned(assetId) {
  const core = useCoreSession.getState().state;
  const slot = (core?.inventory || []).find((item) => item.assetId === assetId);
  return slot?.quantity ?? 0;
}

function selectedAsset() {
  return state.assets[state.selected] || state.assets[0];
}

function coatSnapshot() {
  const core = useCoreSession.getState().state || {};
  const inventory = (Array.isArray(core.inventory) ? core.inventory : [])
    .map((item) => ({ assetId: item.assetId, quantity: Number(item.quantity || 0) }))
    .filter((item) => item.quantity > 0);
  const used = inventory.reduce((sum, item) => sum + item.quantity, 0);
  return {
    inventory,
    used,
    capacity: Number(core.inventoryCapacity ?? 0),
    cash: moneyAmount(core.cash),
    debt: moneyAmount(core.debt),
  };
}

/**
 * One deterministic objective line for the market screen.
 * Debt comes first, then a full coat, then thin cash.
 *
 * @param {Object} input
 * @param {string} input.status - core session status
 * @param {number} input.debt - money owed
 * @param {number} input.cash - spendable cash
 * @param {number} input.inventoryUsed - occupied coat slots
 * @param {number} input.capacity - total coat slots
 * @returns {{ id: string, text: string }}
 */
export function marketObjective({ status, debt, cash, inventoryUsed, capacity } = {}) {
  if (status !== 'online') {
    return { id: 'offline', text: 'OFFLINE // NO LIVE STREET PRICES' };
  }
  const owed = Number(debt) || 0;
  const available = Number(cash) || 0;
  const used = Number(inventoryUsed) || 0;
  const slots = Number(capacity) || 0;
  if (owed > 0) {
    return { id: 'debt', text: `COVER DEBT // $${owed.toFixed(2)} DUE` };
  }
  if (slots > 0 && used >= slots) {
    return { id: 'coat_full', text: 'COAT FULL // SELL BEFORE YOU BUY' };
  }
  if (available < LOW_CASH_THRESHOLD) {
    return { id: 'low_cash', text: 'LOW CASH // SELL STOCK TO REBUY' };
  }
  return { id: 'clear', text: 'CLEAR // NO DEBT, COAT HAS ROOM' };
}

function currentObjective() {
  const session = useCoreSession.getState();
  const coat = coatSnapshot();
  return marketObjective({
    status: session.status,
    debt: coat.debt,
    cash: coat.cash,
    inventoryUsed: coat.used,
    capacity: coat.capacity,
  });
}

function drawTop(ctx, safe) {
  const mobile = safe.width < 500;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width, TERMINAL.GREEN_DARK);
  text(ctx, 'DOPEOS // MARKET', 8, 4, TERMINAL.TEXT);
  if (!mobile) text(ctx, 'BRONX // 149TH', 216, 4, TERMINAL.TEXT_DIM);
  const core = useCoreSession.getState();
  const label = core.status === 'online' ? (state.loading ? 'SYNC' : 'MARKET OPEN') : 'OFFLINE';
  const deskW = 44;
  const deskX = safe.width - deskW - 8;
  DopeUI.Button(ctx, deskX, 2, deskW, 20, 'DESK', 'normal');
  interactiveRegistry.register({
    id: 'market-back',
    x: deskX,
    y: 2,
    w: deskW,
    h: 20,
    onClick: () => navigateTo('dopeos_home'),
  });
  text(ctx, label, deskX - 10 - label.length * 8, 4, core.status === 'online' ? TERMINAL.TEXT : TERMINAL.TEXT_DIM);
  if (!mobile) text(ctx, state.status, safe.width - 340, 4, TERMINAL.TEXT_DIM);
}

function drawObjective(ctx, safe) {
  const objective = currentObjective();
  const y = 26;
  ctx.fillStyle = TERMINAL.GREEN_DARKEST;
  ctx.fillRect(0, y, safe.width, 18);
  rule(ctx, 0, y + 18, safe.width, TERMINAL.GREEN_DARK);
  const color = objective.id === 'clear' ? TERMINAL.TEXT_DIM : TERMINAL.TEXT_BRIGHT;
  text(ctx, `OBJECTIVE // ${objective.text}`, 8, y + 2, color);
}

function drawAssets(ctx, x, y, w, h, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'COMMODITIES // STREET PRICES');
  text(
    ctx,
    mobile ? '#  ASSET      BUY    SELL  OWN' : '#  ASSET          BUY      SELL     OWN',
    x + 10,
    y + 24,
    TERMINAL.TEXT_DIM,
  );
  rule(ctx, x + 8, y + 40, w - 16);
  const rowStep = mobile ? 44 : 38;
  const rowH = mobile ? 44 : 34;
  state.assets.forEach((asset, index) => {
    const rowY = y + 54 + index * rowStep;
    const selected = index === state.selected;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, rowH - 2);
    }
    const buy = money(asset.buy?.unitPrice);
    const sell = money(asset.sell?.unitPrice);
    const line = mobile
      ? `${selected ? '>' : ' '} ${index + 1} ${asset.name.slice(0, 9).padEnd(9)} ${buy.padStart(7)} ${sell.padStart(7)} ${String(owned(asset.id)).padStart(3)}`
      : `${selected ? '>' : ' '} ${index + 1} ${asset.name.padEnd(12).slice(0, 12)} ${buy.padStart(8)} ${sell.padStart(8)} ${String(owned(asset.id)).padStart(4)}`;
    text(ctx, line, x + 10, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    text(ctx, `${asset.sub} // HEAT +${asset.buy?.heatDelta ?? 0}`, x + 10, rowY + 17, TERMINAL.TEXT_DIM);
    interactiveRegistry.register({
      id: `market-asset-${asset.id}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: rowH,
      onClick: () => {
        state.selected = index;
        state.quote = null;
        state.quoteError = null;
        refreshQuote();
      },
    });
  });
}

function drawCoat(ctx, x, y, w, h, mobile = false) {
  const coat = coatSnapshot();
  const capacity = coat.capacity > 0 ? coat.capacity : '--';
  DopeUI.Panel(ctx, x, y, w, h, `YOUR COAT // ${coat.used}/${capacity}`);
  text(ctx, `CASH ${money({ amount: coat.cash.toFixed(2) })}`, x + 10, y + 26, TERMINAL.TEXT);
  const debtLabel = coat.debt > 0 ? `DEBT ${money({ amount: coat.debt.toFixed(2) })}` : 'DEBT CLEAR';
  text(ctx, debtLabel, x + w - 10 - debtLabel.length * 8, y + 26, coat.debt > 0 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
  text(ctx, 'ASSET          QTY', x + 10, y + 44, TERMINAL.TEXT_DIM);
  rule(ctx, x + 8, y + 54, w - 16);

  const maxRows = mobile ? 4 : 10;
  const more = coat.inventory.length - maxRows;
  const rows = coat.inventory.slice(0, more > 0 ? maxRows - 1 : maxRows);
  if (!coat.inventory.length) {
    text(ctx, 'COAT EMPTY // BUY LOW', x + 10, y + 64, TERMINAL.TEXT_DIM);
  }
  rows.forEach((item, index) => {
    const rowY = y + 64 + index * 16;
    const asset = state.assets.find((entry) => entry.id === item.assetId);
    const sell = money(asset?.sell?.unitPrice);
    const label = `${shortName(item.assetId).slice(0, 12).padEnd(12)} x${String(item.quantity).padStart(2)}`;
    text(ctx, label, x + 10, rowY, TERMINAL.TEXT);
    text(ctx, sell, x + w - 18 - sell.length * 8, rowY, TERMINAL.TEXT_DIM);
    interactiveRegistry.register({
      id: `market-hold-${item.assetId}`,
      x: x + 6,
      y: rowY - 3,
      w: w - 12,
      h: 15,
      onClick: () => {
        const nextIndex = state.assets.findIndex((entry) => entry.id === item.assetId);
        if (nextIndex < 0) return;
        state.selected = nextIndex;
        state.side = 'SELL';
        state.quote = null;
        state.quoteError = null;
        refreshQuote();
      },
    });
  });
  if (more > 0) {
    text(ctx, `+${more} MORE IN COAT`, x + 10, y + 64 + (maxRows - 1) * 16, TERMINAL.TEXT_DIM);
  }
}

function drawTradeBar(ctx, x, y, w, h, mobile = false) {
  const asset = selectedAsset();
  const quote = state.quote;
  DopeUI.Panel(ctx, x, y, w, h, `ORDER // ${asset?.name || '--'}`);
  const unit = money(quote?.unitPrice);
  const total = money(quote?.total);

  if (mobile) {
    text(ctx, `UNIT ${unit}   TOTAL ${total}`, x + 10, y + 26, TERMINAL.TEXT);
    text(ctx, 'VS LAST TRADE', x + 10, y + 44, TERMINAL.TEXT_DIM);
    text(ctx, deltaLabel(quote), x + 130, y + 44, TERMINAL.TEXT_BRIGHT);
    text(ctx, 'QTY', x + 10, y + 66, TERMINAL.TEXT_DIM);
    drawStepper(ctx, x + 42, y + 76, 176);
    const buttonY = y + 110;
    const buttonW = (w - 30) / 2;
    drawTradeButtons(ctx, x + 10, buttonY, buttonW);
  } else {
    text(ctx, `UNIT ${unit}`, x + 10, y + 28, TERMINAL.TEXT);
    text(ctx, `TOTAL ${total}`, x + 10, y + 46, TERMINAL.TEXT);
    text(ctx, 'VS LAST TRADE', x + 10, y + 64, TERMINAL.TEXT_DIM);
    text(ctx, deltaLabel(quote), x + 130, y + 64, TERMINAL.TEXT_BRIGHT);
    text(ctx, 'QTY', x + 220, y + 28, TERMINAL.TEXT_DIM);
    drawStepper(ctx, x + 220, y + 38, 156);
    drawTradeButtons(ctx, x + w - 206, y + 24, 186);
  }

  const footer = state.error
    ? state.error
    : state.quoteError
      ? state.quoteError
      : state.receipt?.detail
        ? `RECEIPT // ${state.receipt.detail}`
        : state.loading
          ? 'SYNCING STREET...'
          : 'QUOTE REFRESHES // CONFIRM BEFORE YOU DEAL';
  text(
    ctx,
    footer.slice(0, mobile ? 46 : 62),
    x + 10,
    y + h - 12,
    state.error || state.quoteError ? TERMINAL.TEXT_BRIGHT : state.receipt ? TERMINAL.TEXT : TERMINAL.TEXT_DIM,
  );
}

function drawStepper(ctx, x, y, w) {
  const decW = 40;
  const incW = 40;
  const valueW = w - decW - incW - 12;
  DopeUI.Button(ctx, x, y, decW, 26, '[-]', state.qty > 1 ? 'normal' : 'disabled');
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(x + decW + 6, y, valueW, 26);
  ctx.strokeStyle = TERMINAL.GREEN;
  ctx.strokeRect(x + decW + 6, y, valueW, 26);
  text(ctx, String(state.qty).padStart(2, '0'), x + decW + 6 + valueW / 2 - 8, y + 6, TERMINAL.TEXT_BRIGHT);
  DopeUI.Button(ctx, x + decW + valueW + 12, y, incW, 26, '[+]', state.qty < MAX_QTY ? 'normal' : 'disabled');
  interactiveRegistry.register({
    id: 'market-qty-dec',
    x,
    y,
    w: decW,
    h: 26,
    onClick: () => {
      if (state.qty <= 1) return;
      state.qty -= 1;
      state.quote = null;
      refreshQuote();
    },
  });
  interactiveRegistry.register({
    id: 'market-qty-inc',
    x: x + decW + valueW + 12,
    y,
    w: incW,
    h: 26,
    onClick: () => {
      if (state.qty >= MAX_QTY) return;
      state.qty += 1;
      state.quote = null;
      refreshQuote();
    },
  });
}

function drawTradeButtons(ctx, x, y, w) {
  const gap = 8;
  const buttonW = (w - gap) / 2;
  const buyEnabled = useCoreSession.getState().status === 'online' && !state.loading;
  const sellEnabled = buyEnabled;
  DopeUI.Button(ctx, x, y, buttonW, 44, 'BUY', buyEnabled ? 'hover' : 'disabled');
  DopeUI.Button(ctx, x + buttonW + gap, y, buttonW, 44, 'SELL', sellEnabled ? 'normal' : 'disabled');
  interactiveRegistry.register({
    id: 'market-buy',
    x,
    y,
    w: buttonW,
    h: 44,
    onClick: () => openTrade('BUY'),
  });
  interactiveRegistry.register({
    id: 'market-sell',
    x: x + buttonW + gap,
    y,
    w: buttonW,
    h: 44,
    onClick: () => openTrade('SELL'),
  });
}

async function refreshQuote() {
  const asset = selectedAsset();
  if (!asset) return;
  const serial = ++quoteSeq;
  state.quoteError = null;
  try {
    const quote = await useCoreSession.getState().quoteTrade(asset.id, state.side.toLowerCase(), state.qty);
    if (serial !== quoteSeq) return;
    state.quote = quote;
  } catch (error) {
    if (serial !== quoteSeq) return;
    state.quote = null;
    state.quoteError = coreErrorMessage(error);
  }
}

async function openTrade(side) {
  const asset = selectedAsset();
  if (!asset || useCoreSession.getState().status !== 'online') return;
  trackEvent('trade_start', { assetId: asset.id, side: side.toLowerCase(), source: 'market_view' });
  state.side = side;
  state.tradeOpen = true;
  state.error = null;
  state.receipt = null;
  state.quote = null;
  state.quoteError = null;
  await refreshQuote();
}

export async function loadMarket(force = false) {
  if (loadPromise) return loadPromise;
  if (state.loadedOnce && !force) return null;
  state.loadedOnce = true;
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    try {
      const content = await useCoreSession.getState().loadContent();
      const assets = (content?.assets || [])
        .filter((asset) => asset.market === 'public' && asset.tradable !== false)
        .map(assetFromContent);
      state.assets = assets.length ? assets : FALLBACK_ASSETS;
      const quoted = await Promise.all(
        state.assets.map(async (asset) => {
          const [buy, sell] = await Promise.all([
            useCoreSession.getState().quoteTrade(asset.id, 'buy', 1),
            useCoreSession.getState().quoteTrade(asset.id, 'sell', 1),
          ]);
          return { ...asset, buy, sell };
        }),
      );
      state.assets = quoted;
      state.status = 'QUOTES';
      await refreshQuote();
    } catch (error) {
      state.status = 'OFFLINE';
      state.error = coreErrorMessage(error);
      state.quote = null;
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

async function executeTrade() {
  const asset = selectedAsset();
  if (!asset || !state.quote?.canExecute || state.loading) return;
  state.loading = true;
  state.error = null;
  try {
    const side = state.side;
    const qty = state.qty;
    const heatBefore = Number(useCoreSession.getState().state?.heat ?? 0);
    const result = await useCoreSession.getState().executeTrade(asset.id, side.toLowerCase(), qty);
    const heatAfter = Number(result.state?.heat);
    const heatDelta = Number.isFinite(heatAfter) ? heatAfter - heatBefore : null;
    state.receipt = {
      kind: 'trade',
      detail: `${side} ${qty} ${asset.name} // ${money(result.trade?.total)}${heatDelta == null ? '' : ` // HEAT +${heatDelta}`}`,
    };
    state.tradeOpen = false;
    state.quote = null;
    await loadMarket(true);
  } catch (error) {
    state.error = coreErrorMessage(error);
    state.quote = null;
  } finally {
    state.loading = false;
  }
}

export function _resetMarketState() {
  state.assets = FALLBACK_ASSETS;
  state.selected = 0;
  state.status = 'NOT SYNCED';
  state.loading = false;
  state.loadedOnce = false;
  state.tradeOpen = false;
  state.side = 'BUY';
  state.qty = 1;
  state.quote = null;
  state.quoteError = null;
  state.receipt = null;
  state.error = null;
  loadPromise = null;
  quoteSeq = 0;
}

registerView('market', function renderMarket(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  if (!state.loadedOnce) loadMarket();
  drawTop(ctx, safe);
  drawObjective(ctx, safe);
  const mobile = safe.width < 500;
  if (mobile) {
    drawAssets(ctx, 8, 50, safe.width - 16, 230, true);
    drawCoat(ctx, 8, 288, safe.width - 16, 124, true);
    drawTradeBar(ctx, 8, 420, safe.width - 16, 172, true);
  } else {
    drawAssets(ctx, 8, 50, 376, 236);
    drawCoat(ctx, 392, 50, 240, 236);
    drawTradeBar(ctx, 8, 290, 624, 104);
  }

  if (state.tradeOpen) {
    interactiveRegistry.clear();
    const asset = selectedAsset();
    const hits = drawTradeDialog(ctx, safe, asset, state, state.quote, {
      cancel: () => { state.tradeOpen = false; },
      execute: executeTrade,
    });
    hits.forEach((item) => interactiveRegistry.register(item));
  }
});

export default 'market';
